import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Input from '@/components/Input/Input';
import { InputStates } from '@/components/Input/Input.types';
import Modal from '@/components/Modal/Modal';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useUpdateGroup, useUpdateGroupName } from '@/services/groups/groups.service';
import styles from './EditStoreGroupName.module.css';
import { EditStoreGroupNameProps } from './EditStoreGroupName.types';
import RequestProcessingModal from '@/components/Modal/RequestProcessingModal/RequestProcessingModal';
import { useRequestProcessingModal } from '@/hooks/useRequestProcessingModal';

const EditStoreGroupName: FC<EditStoreGroupNameProps> = ({ isOpen, onClose, storeGroupToEdit, storeGroups }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [groupName, setGroupName] = useState(storeGroupToEdit?.name || '');
  const [inputError, setInputError] = useState('');
  const existingGroupNames = useMemo(() => storeGroups?.map((group) => group.name), [storeGroups]);
  const { isModalOpen, modalType, showTimeoutModal, showErrorModal, handleCloseAndRefresh } = useRequestProcessingModal(
    { onRefresh: () => window.location.reload() },
  );

  useEffect(() => {
    if (storeGroupToEdit?.name) setGroupName(storeGroupToEdit.name);
  }, [storeGroupToEdit]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (existingGroupNames.includes(value)) {
      setInputError('There is an existing group with this name.');
    } else if (value.length > 254) {
      setInputError('The selected name is too long.');
    } else {
      setInputError('');
    }
    setGroupName(event.target.value);
  };

  const { mutate } = useUpdateGroupName();
  const handleSave = () => {
    mutate(
      { newName: groupName, groupId: storeGroupToEdit!.id || '' },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['store-groups']);
          showToast({
            label: t('editStoreGroupNameModal.successMessage', { groupName: storeGroupToEdit?.name }),
            id: 'updateGroupName',
            toastType: ToastTypes.Success,
          });
          onClose?.();
        },
        onError: (error: unknown) => {
          if (error instanceof Error && error.message === 'API Gateway request timed out') {
            showTimeoutModal();
            return;
          }
          showErrorModal();
        },
      },
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        headerContent={t('editStoreGroupNameModal.headerText')}
        bodyContent={
          <div className={styles.body}>
            <Input
              onChange={handleChange}
              onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
              value={groupName}
              id="editName"
              label={t('editStoreGroupNameModal.storeGroupNameLabel')}
              subText={inputError || undefined}
              state={inputError !== '' ? InputStates.Error : undefined}
            />
          </div>
        }
        footerContent={
          <div className={styles.buttons}>
            <Button variant={ButtonVariants.Secondary} onClick={onClose} type="button">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              type="button"
              disabled={groupName.length === 0 || inputError.length !== 0 || groupName === storeGroupToEdit?.name}
            >
              {t('common.confirm')}
            </Button>
          </div>
        }
      />
      {isModalOpen && <RequestProcessingModal isOpen={isModalOpen} type={modalType} onClose={handleCloseAndRefresh} />}
    </>
  );
};

export default EditStoreGroupName;
